import { createFeatureSelector, createSelector } from '@ngrx/store';
import { chatFeatureKey, State } from './chat.reducer';
import { ChatMessage, LoadingStatus } from '@qvs/core';
import { DateUtils } from '~/shared/utils/date.utils';
import { selectCrmContact, selectCrmMyContacts } from '@qvs/core/user';

export const selectChatFeature = createFeatureSelector<State>(chatFeatureKey);

export const selectChatSettings = createSelector(selectChatFeature, (state) => state.settings);

export const selectAllChatMessages = createSelector(selectChatFeature, (state) => state.chat.messages);
export const selectChatAuthorId = createSelector(selectChatFeature, (state) => state.chat.authorId);

export const selectChatIntendUserId = createSelector(selectChatFeature, (state) =>
  state.chat.intendedUserIds ? state.chat.intendedUserIds[0] : null
);

export const selectChatOutOfOfficeMessage = createSelector(selectChatFeature, (state) => state.chat.outOfOfficeMessage);

export const selectChatIntendUser = createSelector(
  selectChatIntendUserId,
  selectCrmContact,
  selectCrmMyContacts,
  (intendUserId, crmPerson1, contacts) => {
    if (!intendUserId) {
      return null;
    }
    if (crmPerson1 && intendUserId === crmPerson1.staffId) {
      return crmPerson1;
    }
    return contacts ? contacts.find((contact) => contact.staffId === intendUserId) : null;
  }
);

export const selectChatMessagesOrderByDateDesc = createSelector(selectAllChatMessages, (messages) =>
  messages.filter(() => true).sort((a, b) => DateUtils.new(a.createdAt).diff(b.createdAt))
);

export const selectChatMessages = createSelector(
  selectChatMessagesOrderByDateDesc,
  selectChatAuthorId,
  selectChatIntendUserId,
  (messages, authorId, intendUserId) =>
    messages.filter(
      (message) =>
        message &&
        ((message.intendedUserId === authorId && message.authorId === intendUserId) ||
          (message.authorId === authorId && message.intendedUserId === intendUserId))
    )
);

export const selectAllChatMessagesReceived = createSelector(
  selectChatMessagesOrderByDateDesc,
  selectChatAuthorId,
  (messages, authorId) => messages.filter((message) => message && message.authorId !== authorId)
);

export const selectNotReadMessages = createSelector(selectChatFeature, (state) => state.notReadMessages);

export const selectChatIsLoading = createSelector(selectChatFeature, (state) => !!state.chatIsLoading);

export const selectChatHasErrors = createSelector(selectChatFeature, (state) => state.chatHasErrors);

export const selectThreads = createSelector(selectChatFeature, (state) => state.threads);

export const selectNotReadMessagesAsAuthorCountMap = createSelector(selectNotReadMessages, selectThreads, (messages, threads) => {
  const notReadMessagesKeyMap = {};
  if (threads) {
    Object.keys(threads).forEach((authorId) => (notReadMessagesKeyMap[authorId] = threads[authorId].unreadMessageCount));
  }
  messages.forEach((message) => {
    if (notReadMessagesKeyMap[message.authorId] === undefined) {
      notReadMessagesKeyMap[message.authorId] = 0;
    }
    notReadMessagesKeyMap[message.authorId]++;
  });

  Object.keys(notReadMessagesKeyMap).forEach((authorId) => {
    if (notReadMessagesKeyMap[authorId] === 0) {
      delete notReadMessagesKeyMap[authorId];
    }
  });

  return notReadMessagesKeyMap;
});

export const countNotReadMessages = createSelector(selectNotReadMessagesAsAuthorCountMap, (notReadMessagesKeyMap) => {
  let count = 0;
  if (!notReadMessagesKeyMap) {
    return count;
  }
  Object.keys(notReadMessagesKeyMap).forEach((authorId) => {
    count = count + notReadMessagesKeyMap[authorId];
  });
  return count;
});

const selectThreadsLoaded = createSelector(selectChatFeature, (state) => state.threadsLoadingStatus === LoadingStatus.SUCCEEDED);

export const countNotReadMessagesAfterSuccessfulInitialLoading = createSelector(
  countNotReadMessages,
  selectThreadsLoaded,
  (count, threadsLoaded) => (threadsLoaded ? count : null)
);
export const selectChatMessageById = (id: number) =>
  createSelector(selectAllChatMessages, (messages: ChatMessage[]) => messages.find((message) => message.id === id));
